import { Controller } from "@hotwired/stimulus"
import { syncGateReason } from "../lib/gate_reason"
import { SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"

// The ONE owner of an auth submit's `disabled` while a required Terms box is
// on the form (owner ruling 2026-10-26: Sign Up stays off until the Terms box
// is ticked). The button opens only when:
//
//   1. every `terms` checkbox is ticked, AND
//   2. the form's card-dirty controller (BUTTON-9, when the page wires one as
//      this controller's outlet) says something was entered.
//
// When both fail the Terms reason wins — it is the one thing the agent can
// act on with a single click. card-dirty keeps computing `dirty` but is given
// no submit target on these forms, so exactly one controller ever writes the
// button's disabled state; lib/gate_reason writes the reason as the tooltip
// and the accessible name, and never re-opens a button the busy spinner
// (native-submit-busy, BUTTON-8) has marked aria-busy.
//
// The checkbox keeps its browser `required` and the server keeps
// RegistrationsController#ensure_tos_agreement / the sign-in check as the
// backstop; nothing here changes what the form submits.
//
//   = form_with ..., data: { controller: 'card-dirty terms-gate native-submit-busy',
//                            action: 'input->terms-gate#sync change->terms-gate#sync',
//                            'terms-gate-card-dirty-outlet': '#signup-form' }
//   = check_box_tag ..., data: { 'terms-gate-target': 'terms' }
//   = auth_submit_button form, 'Get Started', data: { 'terms-gate-target': 'submit' }
export const TERMS_REASON = "Agree to the Terms first"

export default class extends Controller {
  static targets = ["terms", "submit"]
  static outlets = ["card-dirty"]
  static values = { reason: { type: String, default: TERMS_REASON } }

  connect() {
    this.sync()
    // card-dirty snapshots its baseline on the next frame (it connects first,
    // so its frame runs first); re-gate once its `dirty` answer is real.
    this._frame = requestAnimationFrame(() => this.sync())
  }

  disconnect() {
    cancelAnimationFrame(this._frame)
  }

  cardDirtyOutletConnected() {
    this.sync()
  }

  termsTargetConnected() {
    this.sync()
  }

  submitTargetConnected() {
    this.sync()
  }

  sync() {
    const reason = this._closedReason()
    this.submitTargets.forEach((button) => syncGateReason(button, { ready: reason === null, reason }))
  }

  _closedReason() {
    if (this.termsTargets.some((box) => !box.checked)) return this.reasonValue
    if (this.hasCardDirtyOutlet && !this.cardDirtyOutlet.dirty) return SAVE_UNCHANGED_REASON
    return null
  }
}
